/**
 * Centralized Axios client for all frontend API requests.
 *
 * Base URL resolution (in priority order):
 *  1. REACT_APP_API_BASE_URL environment variable  – set this for dev/staging/prod
 *     when the backend runs on a different origin (e.g. http://localhost:5000).
 *  2. Empty string (default) – falls back to same-origin relative paths, which
 *     avoids CORS pre-flight requests and works with the CRA proxy (package.json
 *     "proxy" field) in development.
 *
 * Example .env.local overrides:
 *   REACT_APP_API_BASE_URL=http://localhost:5000   # backend on port 5000
 *   REACT_APP_API_BASE_URL=https://api.example.com # production API domain
 */

import axios from 'axios';

const baseURL = process.env.REACT_APP_API_BASE_URL || '';

const apiClient = axios.create({ baseURL });

// Attach stored JWT token to every request automatically.
apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

// Improve network-error visibility in the browser console.
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (!error.response) {
            // Network error / ERR_CONNECTION_REFUSED
            const resolvedBase = baseURL || '(same-origin / CRA proxy)';
            console.error(
                `[apiClient] Network error – could not reach the backend.\n` +
                `  Resolved base URL : ${resolvedBase}\n` +
                `  How to fix:\n` +
                `    • Make sure the backend server is running.\n` +
                `    • If the backend is on a different port, set REACT_APP_API_BASE_URL in your .env.local.\n` +
                `    • Example: REACT_APP_API_BASE_URL=http://localhost:5000`,
                error
            );
        }
        return Promise.reject(error);
    }
);

export default apiClient;
